import { useMemo, useRef, useState } from 'react';

import { Box, Fade, IconButton, Slide } from '@mui/material';
import { hasActiveModuleGrant } from '@realadvisor/constants';
import { X } from 'lucide-react';

import type { GetLeadDetailsQuery } from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TwoPaneLayout } from '@modules/_shared/components/TwoPaneLayout';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { LeadDetailsCardStack } from '@modules/lead-details/components/LeadDetailsCardStack';
import LeadFeedV2 from '@modules/lead-details/components/LeadFeedV2';
import { useCmaPreview } from '@modules/lead-details/hooks/useCmaPreview';
import type { LeadPermissions } from '@modules/lead-details/hooks/useLeadPermissions';
import { LeadDetailsMap } from '@modules/lead-details/LeadDetailsMap';
import { getLeadMapProperty } from '@modules/lead-details/utils/getLeadMapProperty';
import CMAReportEditorWorkflow from '@modules/reports/cma-reports/CMAReportEditor/CMAReportEditorWorkflow';
import { MODULES_BROKERAGE } from '@shared/constants/module';

type Lead = NonNullable<GetLeadDetailsQuery['leads_by_pk']>;

type DesktopLeadDetailsLayoutProps = {
  lead: Lead;
  leadId: string;
  permissions: LeadPermissions;
  refetch: () => Promise<unknown>;
  onAssignmentChange: (keepOpen: boolean) => void;
};

export const DesktopLeadDetailsLayout = ({
  lead,
  leadId,
  permissions,
  refetch,
  onAssignmentChange,
}: DesktopLeadDetailsLayoutProps) => {
  const { canView, isAdmin, restrictedAgentView } = permissions;
  const { me } = useAppData();
  const isBrokerageModuleIncluded = hasActiveModuleGrant(
    me?.module_grants,
    MODULES_BROKERAGE,
  );
  const leadAgents = useMemo(() => lead.lead_agents ?? [], [lead.lead_agents]);
  const mapProperty = getLeadMapProperty(lead);
  const requiresAgentSelection = isAdmin && leadAgents.length > 1;
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const {
    cmaReport,
    isPreviewOpen,
    isLoading: cmaPreviewLoading,
    apolloError,
    openPreview,
    closePreview,
    clearError,
  } = useCmaPreview();

  const feedScrollRef = useRef<HTMLDivElement>(null);

  const leftPane = (
    <LeadDetailsCardStack
      lead={lead}
      leadId={leadId}
      isAdmin={isAdmin}
      isBrokerageModuleIncluded={isBrokerageModuleIncluded}
      restrictedAgentView={restrictedAgentView}
      canView={canView}
      selectedAgentId={selectedAgentId}
      setSelectedAgentId={setSelectedAgentId}
      requiresAgentSelection={requiresAgentSelection}
      refetch={refetch}
      onAssignmentChange={onAssignmentChange}
      onCmaPreviewGenerated={openPreview}
      isPreviewOpen={isPreviewOpen}
      onClosePreview={closePreview}
      requirePropertyForOtherLeads
    />
  );

  const rightPane = (
    <>
      {/* Map and Feed - hidden when CMA preview is shown */}
      <Fade in={!isPreviewOpen} timeout={300} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {mapProperty && (
            <Box sx={{ minWidth: 0 }}>
              <LeadDetailsMap
                key={canView ? 'full' : 'preview'}
                property={mapProperty}
                initialTab={lead.potential_development ? 'zoning' : 'satellite'}
                previewMode={!canView}
                actionsEnabled={canView && !restrictedAgentView}
              />
            </Box>
          )}
          <LeadFeedV2
            lead_id={lead.id}
            canView={canView}
            scrollParentRef={feedScrollRef}
          />
        </Box>
      </Fade>

      {/* CMA Preview Panel - slides in from right */}
      <Slide direction="left" in={isPreviewOpen} timeout={400} unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
            }}
          >
            <IconButton
              onClick={closePreview}
              size="small"
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
                transition: 'background-color 0.15s ease',
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          {!cmaPreviewLoading && cmaReport && (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <CMAReportEditorWorkflow cmaReport={cmaReport} previewOnly />
            </Box>
          )}
        </Box>
      </Slide>
    </>
  );

  return (
    <>
      <TwoPaneLayout
        leftPane={leftPane}
        rightPane={rightPane}
        rightPaneRef={feedScrollRef}
        rightPaneSx={{ position: 'relative' }}
      />
      <MutationErrorSnackbar error={apolloError} onClose={clearError} />
    </>
  );
};
